import { Component } from '@theme/component';
import { debounce, prefersReducedMotion } from '@theme/utilities';

/**
 * Full-width header search strip (replaces modal). Positions predictive results under the bar edge.
 *
 * @extends {Component}
 */
class HeaderSearchBar extends Component {
  /** @type {AbortController | null} */
  #docPointer = null;

  /** @type {boolean} */
  #scrollLockActive = false;

  connectedCallback() {
    super.connectedCallback();
    window.addEventListener('resize', this.#syncResultsTop, { passive: true });
    window.addEventListener('scroll', this.#syncResultsTop, { passive: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#syncResultsTop);
    window.removeEventListener('scroll', this.#syncResultsTop);
    this.#detachDocumentClose();
    this.#releaseScrollLock();
  }

  #syncResultsTop = debounce(() => {
    if (!this.hasAttribute('open')) return;

    const headerRow = this.querySelector('.predictive-search-form__header');
    if (!(headerRow instanceof HTMLElement)) return;

    this.style.setProperty('--header-search-results-top', `${headerRow.getBoundingClientRect().bottom}px`);
  }, 16);

  #attachDocumentClose() {
    this.#detachDocumentClose();
    const controller = new AbortController();
    this.#docPointer = controller;

    document.addEventListener(
      'pointerdown',
      (event) => {
        if (!this.hasAttribute('open')) return;
        const target = event.target;
        if (!(target instanceof Node)) return;
        if (this.contains(target)) return;
        if (target instanceof Element && target.closest('.search-action')) return;

        this.closeBar();
      },
      { capture: true, signal: controller.signal }
    );
  }

  #detachDocumentClose() {
    this.#docPointer?.abort();
    this.#docPointer = null;
  }

  #applyScrollLock() {
    if (this.#scrollLockActive) return;
    document.documentElement.setAttribute('scroll-lock', '');
    this.#scrollLockActive = true;
  }

  #releaseScrollLock() {
    if (!this.#scrollLockActive) return;
    document.documentElement.removeAttribute('scroll-lock');
    this.#scrollLockActive = false;
  }

  /**
   * Opens the bar and focuses the search field, or closes when already open (toggle).
   */
  openAndFocus() {
    if (this.hasAttribute('open')) {
      this.closeBar();
      return;
    }

    this.setAttribute('open', '');
    this.#applyScrollLock();
    this.#syncResultsTop();
    this.#attachDocumentClose();

    this.scrollIntoView({ behavior: prefersReducedMotion() ? 'instant' : 'smooth', block: 'nearest' });

    requestAnimationFrame(() => {
      const input = this.querySelector('.search-input');
      if (input instanceof HTMLInputElement) {
        input.focus();
      }
      this.#syncResultsTop();
    });
  }

  /**
   * Hides the bar and clears predictive search UI.
   */
  closeBar() {
    this.#detachDocumentClose();
    this.#releaseScrollLock();
    this.removeAttribute('open');

    const predictive = this.querySelector('predictive-search-component');
    if (!(predictive instanceof HTMLElement)) return;

    predictive.classList.remove('predictive-search--expanded');
    for (const cls of [...predictive.classList]) {
      if (cls.startsWith('color-')) predictive.classList.remove(cls);
    }

    const resetSearch = /** @type {any} */ (predictive).resetSearch;
    if (typeof resetSearch === 'function') {
      resetSearch.call(predictive, false);
    }
  }
}

if (!customElements.get('header-search-bar')) {
  customElements.define('header-search-bar', HeaderSearchBar);
}
